import React, { useState } from 'react';
import Sidebar from '../../components/shared/Sidebar';
import Header from '../../components/shared/Header';
import OrderFilters from '../../components/orders/OrderFilters';
import OrderTable from '../../components/orders/OrderTable';
import './Orders.css';

interface Order {
  id: string;
  customer: string;
  restaurant: string;
  items: number;
  amount: string;
  status: 'completed' | 'preparing' | 'pending' | 'ready' | 'cancelled';
  dateTime: string;
}

const Orders: React.FC = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('All Status');
  const [restaurantFilter, setRestaurantFilter] = useState('All Restaurants');
  const [searchQuery, setSearchQuery] = useState('');

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  // Sample order data
  const orders: Order[] = [
    { id: 'ORD-1234', customer: 'Sarah Johnson', restaurant: 'Pizza Palace', items: 3, amount: '$45.99', status: 'completed', dateTime: '2025-11-07 14:32' },
    { id: 'ORD-1233', customer: 'Mike Chen', restaurant: 'Burger Spot', items: 2, amount: '$32.50', status: 'preparing', dateTime: '2025-11-07 14:20' },
    { id: 'ORD-1232', customer: 'Emma Davis', restaurant: 'Sushi House', items: 5, amount: '$67.20', status: 'pending', dateTime: '2025-11-07 14:15' },
    { id: 'ORD-1231', customer: 'Tom Wilson', restaurant: 'Taco Express', items: 4, amount: '$28.75', status: 'ready', dateTime: '2025-11-07 14:08' },
    { id: 'ORD-1230', customer: 'Lisa Brown', restaurant: 'Pasta Kitchen', items: 2, amount: '$54.30', status: 'cancelled', dateTime: '2025-11-07 14:00' },
    { id: 'ORD-1229', customer: 'James Lee', restaurant: 'Pizza Palace', items: 6, amount: '$89.40', status: 'completed', dateTime: '2025-11-07 13:45' },
    { id: 'ORD-1228', customer: 'Anna Taylor', restaurant: 'Sushi House', items: 3, amount: '$43.20', status: 'preparing', dateTime: '2025-11-07 13:30' },
    { id: 'ORD-1227', customer: 'Chris Martin', restaurant: 'Burger Spot', items: 2, amount: '$25.80', status: 'completed', dateTime: '2025-11-07 13:22' },
  ];

  const totalOrders = 234;
  const ordersPerPage = 8;
  const totalPages = Math.ceil(totalOrders / ordersPerPage);

  const handleSelectOrder = (orderId: string) => {
    setSelectedOrders(prev =>
      prev.includes(orderId)
        ? prev.filter(id => id !== orderId)
        : [...prev, orderId]
    );
  };

  const handleSelectAll = () => {
    if (selectedOrders.length === orders.length) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(orders.map(o => o.id));
    }
  };

  return (
    <div className="dashboard-container">
      <Sidebar 
        collapsed={sidebarCollapsed}
        onToggle={toggleSidebar}
      />
      <div className={`dashboard-main ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        <Header page="orders" />
        <main className="dashboard-content">
          <div className="orders-header-section">
            <div>
              <h1 className="orders-title">Orders</h1>
              <p className="orders-subtitle">Manage customer orders.</p>
            </div>
            <button className="orders-export-btn">
              <span>📥</span> Export Orders
            </button>
          </div>

          <OrderFilters
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            restaurantFilter={restaurantFilter}
            onRestaurantFilterChange={setRestaurantFilter}
          />

          <OrderTable
            orders={orders}
            selectedOrders={selectedOrders}
            onSelectOrder={handleSelectOrder}
            onSelectAll={handleSelectAll}
          />

          <div className="orders-pagination">
            <div className="pagination-info">
              Showing {((currentPage - 1) * ordersPerPage) + 1} to {Math.min(currentPage * ordersPerPage, totalOrders)} of {totalOrders} orders
            </div>
            <div className="pagination-controls">
              <button
                className="pagination-btn"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(prev => prev - 1)}
              >
                Previous
              </button>
              {Array.from({ length: Math.min(3, totalPages) }, (_, i) => {
                const page = i + 1;
                return (
                  <button
                    key={page}
                    className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </button>
                );
              })}
              <button
                className="pagination-btn"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(prev => prev + 1)}
              >
                Next
              </button>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Orders;

